import {
  Avatar,
  Box,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';

import { IAttachment } from '../../../interfaces/IAttachment';

import DeleteIcon from '@mui/icons-material/Delete';
import { InputFile, LinkImage } from './MedicalCertificateForm.styled';
import { BiAddToQueue } from 'react-icons/bi';
import { useMedicalCertificateService } from '../../../hooks/services/useMedicalCertificateService';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';

interface IProps {
  medicalCertificateId: string;
}

const MedicalCertificateFormAttachments = (props: IProps) => {
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [attachments, setAttachments] = useState<IAttachment[]>([]);
  const [loading, setLoading] = useState(false);

  const medicalCertificateService = useMedicalCertificateService();
  const toastr = useToastrVanila();

  useEffect(() => {
    listAttachments();
  }, [props.medicalCertificateId]);

  const getFilename = (attachment: IAttachment) => {
    const url = attachment.attachment;
    const fileName = url?.split('/')[url.split('/').length - 1];

    return fileName?.substring(33, 999);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    sendFile(e.target.files?.item(0) as File);
  };

  const sendFile = async (fileParam: File | null) => {
    if (fileParam) {
      setLoading(true);
      let file = fileParam;

      const blob = new Blob([fileParam]);

      const tamannhoEmBytes = blob.size;

      if (tamannhoEmBytes > 5242880) {
        setLoading(false);
        toastr.error('O tamanho máximo da imagem é 5MB');
        return;
      }

      if (tamannhoEmBytes >= 1000000 && tamannhoEmBytes <= 5242880 && fileParam.type !== 'application/pdf') {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };

        file = await imageCompression(fileParam, options);
      }

      const data = new FormData();

      data.append('attachment', file, fileParam?.name);
      await medicalCertificateService
        .sendAttachment(`${props.medicalCertificateId}`, data)
        .then(async () => {
          setLoading(false);
          listAttachments();
        })
        .catch(error => {
          setLoading(false);
          toastr.error('Ocorreu um erro ao enviar a imagem. Tente novamente mais tarde.');
        });
    }
  };

  const listAttachments = async () => {
    medicalCertificateService
      .listAttachmentsByMedicalCertificateId(props.medicalCertificateId)
      .then(result => {
        setAttachments(result);
      })
      .catch(() => {
        toastr.error('Ocorreu um erro ao listar os anexos. Tente novamente mais tarde.');
      });
  };

  const removeAttachment = async (id: string): Promise<void> => {
    setLoading(true);
    medicalCertificateService
      .removeAttachment(id)
      .then(async () => {
        setLoading(false);
        await listAttachments();
      })
      .catch(() => {
        setLoading(false);
        toastr.error('Ocorreu um erro ao carregar a lista de doenças.');
      });
  };

  return (
    <Box sx={{ padding: '10px 0px 10px 10px' }}>
      <InputFile type="file" ref={inputFile} onChange={onFileChange} accept="application/pdf,image/*" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <h2>Anexos</h2>
        <Tooltip title="Adicionar Anexos" placement="left">
          <IconButton sx={{ width: '50px', height: '50px' }} onClick={() => inputFile.current?.click()}>
            <BiAddToQueue />
          </IconButton>
        </Tooltip>
      </Box>
      {loading ? <LinearProgress /> : <hr />}
      <List dense={true} sx={{ paddingLeft: '20px', paddingRight: '20px' }}>
        {attachments.map(attachment => {
          return (
            <ListItem
              key={attachment.id}
              secondaryAction={
                <Tooltip title="Baixar documento" placement="left">
                  <IconButton edge="end" aria-label="delete" onClick={() => removeAttachment(attachment.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemAvatar>
                <Avatar>
                  <img
                    alt={getFilename(attachment)}
                    style={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'contain',
                    }}
                    src={attachment.attachment}
                  />
                </Avatar>
              </ListItemAvatar>
              <ListItemText>
                <LinkImage target="_blank" href={attachment.attachment}>
                  {getFilename(attachment)}
                </LinkImage>
              </ListItemText>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default MedicalCertificateFormAttachments;
